/**
 * 订单价格计算工具
 * 统一管理所有与订单价格相关的计算逻辑
 */

import { Cmd } from '../_enum'

/**
 * 标准化数值，将0或null转换为undefined
 * @param value 待标准化的值
 * @returns 标准化后的值
 */
export const normalizeValue = (value: number | undefined | null): undefined | number =>
  value === 0 || value == null ? undefined : value

// 使用 ask 价格的订单类型（买入订单）
// 根据现有枚举：BUY=0, BUY_LIMIT=2, BUY_STOP=4
export const BUY_ORDER_TYPES = [Cmd.BUY, Cmd.BUY_LIMIT, Cmd.BUY_STOP] as const

/**
 * 根据订单类型计算当前价格
 * 统一的价格计算逻辑，避免重复
 *
 * @param cmd 订单命令类型
 * @param ask 卖出价格
 * @param bid 买入价格
 * @returns 当前价格（买入订单类型使用ask，卖出订单类型使用bid）
 */
export function calculateOrderCurrentPrice(cmd: number, ask: number, bid: number): number {
  // 订单类型0,2,4为买入订单，使用ask价格；其他为卖出订单，使用bid价格
  return BUY_ORDER_TYPES.includes(cmd as (typeof BUY_ORDER_TYPES)[number]) ? ask : bid
}

/**
 * 判断是否为买入订单类型
 * @param cmd 订单命令类型
 * @returns 是否为买入订单
 */
export function isBuyOrder(cmd: number): boolean {
  return BUY_ORDER_TYPES.includes(cmd as (typeof BUY_ORDER_TYPES)[number])
}

/**
 * 判断是否为卖出订单类型
 * @param cmd 订单命令类型
 * @returns 是否为卖出订单
 */
export function isSellOrder(cmd: number): boolean {
  return !isBuyOrder(cmd)
}

/**
 * 应用报价到订单行（通用版本）
 * @param row 订单行对象
 * @param ask 卖出价格
 * @param bid 买入价格
 */
export function applyQuoteToOrder<
  T extends { CMD: number; ask?: number; bid?: number; CURRENT_PRICE?: number },
>(row: T, ask: number, bid: number): void {
  row.ask = ask
  row.bid = bid
  row.CURRENT_PRICE = calculateOrderCurrentPrice(row.CMD, ask, bid)
}

/**
 * 获取订单类型的国际化键名
 * @param cmd 订单命令类型
 * @returns 国际化键名，用于 t() 函数
 */
export function getOrderTypeI18nKey(cmd: number): string {
  switch (cmd) {
    case Cmd.BUY:
      return 'trading.order.type.buy'
    case Cmd.SELL:
      return 'trading.order.type.sell'
    case Cmd.BUY_LIMIT:
      return 'trading.order.type.buy-limit'
    case Cmd.SELL_LIMIT:
      return 'trading.order.type.sell-limit'
    case Cmd.BUY_STOP:
      return 'trading.order.type.buy-stop'
    case Cmd.SELL_STOP:
      return 'trading.order.type.sell-stop'
    default:
      return 'trading.common.unknown'
  }
}

/**
 * 获取订单类型的显示名称（已弃用，请使用 getOrderTypeI18nKey）
 * @deprecated 请使用 getOrderTypeI18nKey 配合 t() 函数实现多语言支持
 * @param cmd 订单命令类型
 * @returns 订单类型名称
 */
export function getOrderTypeName(cmd: number): string {
  switch (cmd) {
    case Cmd.BUY:
      return 'Buy'
    case Cmd.SELL:
      return 'Sell'
    case Cmd.BUY_LIMIT:
      return 'Buy Limit'
    case Cmd.SELL_LIMIT:
      return 'Sell Limit'
    case Cmd.BUY_STOP:
      return 'Buy Stop'
    case Cmd.SELL_STOP:
      return 'Sell Stop'
    default:
      return `Unknown (${cmd})`
  }
}
